import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Briefcase, FilePdf } from "@phosphor-icons/react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import api from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";
import { getLogoDataUrl } from "@/components/reports/reportUtils";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/permissions";
import { JobStatusSelect } from "@/components/JobStatusSelect";
import JobTimeline from "@/components/JobTimeline";
import JobAssignments from "@/components/JobAssignments";
import JobSessions from "@/components/JobSessions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const STATUS_COLORS = {
  Active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Completed: "bg-blue-100 text-blue-700 border-blue-200",
  Delivered: "bg-violet-100 text-violet-700 border-violet-200",
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  Pending: "bg-amber-100 text-amber-700 border-amber-200",
  "In Progress": "bg-blue-100 text-blue-700 border-blue-200",
  Approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-100 text-red-700 border-red-200",
};

function Stat({ label, value, sub }) {
  return (
    <Card className="p-4 border-slate-200 shadow-none">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <h3 className="font-semibold text-slate-900 mb-3">{title}</h3>
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden overflow-x-auto">{children}</div>
    </div>
  );
}

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdmin(user?.role);

  const jobs = useQuery({ queryKey: ["entity", "jobs"], queryFn: async () => (await api.get(`/jobs`)).data });
  const costs = useQuery({ queryKey: ["entity", "costs"], queryFn: async () => (await api.get(`/costs`)).data });
  const production = useQuery({ queryKey: ["entity", "production"], queryFn: async () => (await api.get(`/production`)).data });
  const external = useQuery({ queryKey: ["entity", "external"], queryFn: async () => (await api.get(`/external`)).data });
  const machines = useQuery({ queryKey: ["entity", "machines"], queryFn: async () => (await api.get(`/machines`)).data });
  const operations = useQuery({ queryKey: ["entity", "operations"], queryFn: async () => (await api.get(`/operations`)).data });
  const team = useQuery({ queryKey: ["entity", "team"], queryFn: async () => (await api.get(`/team`)).data });

  const job = (jobs.data || []).find((j) => j.id === jobId);
  const jobCosts = (costs.data || []).filter((c) => c.job_id === jobId);
  const jobProd = (production.data || []).filter((p) => p.job_id === jobId);
  const jobExt = (external.data || []).filter((e) => e.job_id === jobId);

  const refName = (list, id, field) => {
    const f = (list || []).find((x) => x.id === id);
    return f ? `${f.code} · ${f[field] || ""}` : "—";
  };

  const rupee = (n) => "Rs. " + Number(n || 0).toLocaleString("en-IN");

  const downloadPdf = async () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const logo = await getLogoDataUrl();
    if (logo) {
      try { doc.addImage(logo, "JPEG", 40, 26, 120, 30); } catch (e) {}
      try {
        if (doc.GState && doc.setGState) {
          doc.saveGraphicsState();
          doc.setGState(new doc.GState({ opacity: 0.06 }));
          doc.addImage(logo, "JPEG", W / 2 - 170, H / 2 - 42, 340, 84);
          doc.restoreGraphicsState();
        }
      } catch (e) {}
    }
    doc.setFontSize(16); doc.setFont(undefined, "bold");
    doc.text("Job Sheet", W - 40, 44, { align: "right" });
    doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(120);
    doc.text("Apt Enterprises · Tool Room Workshop", W - 40, 60, { align: "right" });
    doc.setTextColor(0);
    doc.setDrawColor(226, 232, 240);
    doc.line(40, 70, W - 40, 70);

    doc.setFontSize(13); doc.setFont(undefined, "bold");
    doc.text(`${job.code} — ${job.job_name}`, 40, 92);
    doc.setFontSize(10); doc.setFont(undefined, "normal");
    [
      `Client: ${job.client_name || "-"}`,
      `Product Type: ${job.product_type || "-"}`,
      `Status: ${job.job_status || "-"}`,
      `Timeline: ${formatDate(job.start_date)} to ${formatDate(job.end_date)}`,
    ].forEach((t, i) => doc.text(t, 40, 112 + i * 15));

    autoTable(doc, {
      startY: 180,
      head: [["Budget Allocated", "Budget Spent", "Variance", "Profitability"]],
      body: [[rupee(job.budget_allocated), rupee(job.budget_spent), rupee(Number(job.budget_spent) - Number(job.budget_allocated)), `${job.profitability}%`]],
      theme: "grid", headStyles: { fillColor: [37, 99, 235] }, styles: { fontSize: 10 }, margin: { left: 40, right: 40 },
    });

    doc.setFont(undefined, "bold"); doc.setFontSize(11);
    doc.text("Cost Entries", 40, doc.lastAutoTable.finalY + 24);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 32,
      head: [["Cost ID", "Category", "Planned", "Actual", "Variance", "Date"]],
      body: jobCosts.length
        ? jobCosts.map((c) => [c.code, c.cost_category || "-", rupee(c.planned_cost), rupee(c.actual_cost), rupee(c.cost_variance), formatDate(c.date)])
        : [["—", "No cost entries", "", "", "", ""]],
      theme: "striped", headStyles: { fillColor: [71, 85, 105] }, styles: { fontSize: 9 }, margin: { left: 40, right: 40 },
    });

    doc.setFont(undefined, "bold"); doc.setFontSize(11);
    doc.text("External Processes", 40, doc.lastAutoTable.finalY + 24);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 32,
      head: [["Process ID", "Process", "Vendor", "Planned", "Actual", "Status"]],
      body: jobExt.length
        ? jobExt.map((e) => [e.code, e.process_name || "-", e.vendor_name || "-", rupee(e.planned_cost), rupee(e.actual_cost), e.status || "-"])
        : [["—", "No external processes", "", "", "", ""]],
      theme: "striped", headStyles: { fillColor: [71, 85, 105] }, styles: { fontSize: 9 }, margin: { left: 40, right: 40 },
    });

    doc.setFontSize(8); doc.setTextColor(150);
    doc.text(`Generated ${new Date().toLocaleString("en-IN")}`, 40, doc.internal.pageSize.getHeight() - 24);
    doc.text("Apt Enterprises · Tool Room Workshop", W - 40, doc.internal.pageSize.getHeight() - 24, { align: "right" });
    doc.save(`${job.code}_JobSheet.pdf`);
  };

  if (jobs.isLoading) return <div className="text-slate-400 py-20 text-center">Loading...</div>;
  if (!job) return (
    <div className="py-20 text-center text-slate-500">
      Job not found. <button className="text-blue-600 underline" onClick={() => navigate("/jobs")}>Back to Jobs</button>
    </div>
  );

  return (
    <div>
      <button onClick={() => navigate("/jobs")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="job-detail-back">
        <ArrowLeft className="h-4 w-4" /> Back to Jobs
      </button>

      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
            <Briefcase className="h-6 w-6 text-blue-600" weight="bold" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="job-detail-name">{job.job_name}</h1>
              {admin ? (
                <JobStatusSelect jobId={job.id} status={job.job_status} code={job.code} invalidateKeys={[["entity", "jobs"], ["dashboard"]]} />
              ) : (
                job.job_status && <Badge variant="outline" className={STATUS_COLORS[job.job_status] || ""}>{job.job_status}</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              <span className="font-mono">{job.code}</span> · {job.client_name} · {job.product_type}
            </p>
          </div>
        </div>
        <Button onClick={downloadPdf} variant="outline" className="shrink-0" data-testid="download-pdf-btn">
          <FilePdf className="h-4 w-4 mr-1.5" weight="bold" /> Download PDF
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Budget Allocated" value={formatCurrency(job.budget_allocated)} />
        <Stat label="Budget Spent" value={formatCurrency(job.budget_spent)} />
        <Stat label="Profitability" value={`${job.profitability}%`} />
        <Stat label="Timeline" value={formatDate(job.start_date)} sub={`to ${formatDate(job.end_date)}`} />
      </div>

      <Section title="Assigned Machines & Operators">
        <JobAssignments job={job} prodLogs={jobProd} machines={machines.data} team={team.data} />
      </Section>

      {admin && (
        <Section title="Timers & Who Started Them">
          <JobSessions jobId={job.id} machines={machines.data} />
        </Section>
      )}

      {admin && (
        <Section title="Status Timeline">
          <JobTimeline jobId={job.id} />
        </Section>
      )}

      <Section title={`Cost Entries (${jobCosts.length})`}>
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Cost ID</TableHead><TableHead>Category</TableHead><TableHead>Operation</TableHead>
              <TableHead>Planned</TableHead><TableHead>Actual</TableHead><TableHead>Variance</TableHead><TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobCosts.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No cost entries.</TableCell></TableRow>
            ) : jobCosts.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-mono">{c.code}</TableCell>
                <TableCell>{c.cost_category}</TableCell>
                <TableCell>{c.operation_id ? refName(operations.data, c.operation_id, "operation_name") : "—"}</TableCell>
                <TableCell>{formatCurrency(c.planned_cost)}</TableCell>
                <TableCell>{formatCurrency(c.actual_cost)}</TableCell>
                <TableCell className={Number(c.cost_variance) > 0 ? "text-red-600" : "text-emerald-600"}>{formatCurrency(c.cost_variance)}</TableCell>
                <TableCell>{formatDate(c.date)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section title={`Production Logs (${jobProd.length})`}>
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Log ID</TableHead><TableHead>Date</TableHead><TableHead>Machine</TableHead>
              <TableHead>Actual Hrs</TableHead><TableHead>Output</TableHead><TableHead>Approval</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobProd.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No production logs.</TableCell></TableRow>
            ) : jobProd.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-mono">{p.code}</TableCell>
                <TableCell>{formatDate(p.date)}</TableCell>
                <TableCell>{refName(machines.data, p.machine_id, "machine_name")}</TableCell>
                <TableCell>{p.actual_hours || "—"}</TableCell>
                <TableCell>{p.actual_output || "—"}</TableCell>
                <TableCell><Badge variant="outline" className={STATUS_COLORS[p.approval_status || "Pending"] || ""}>{p.approval_status || "Pending"}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section title={`External Processes (${jobExt.length})`}>
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Process ID</TableHead><TableHead>Process</TableHead><TableHead>Vendor</TableHead>
              <TableHead>Planned</TableHead><TableHead>Actual</TableHead><TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobExt.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No external processes.</TableCell></TableRow>
            ) : jobExt.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-mono">{e.code}</TableCell>
                <TableCell>{e.process_name}</TableCell>
                <TableCell>{e.vendor_name || "—"}</TableCell>
                <TableCell>{formatCurrency(e.planned_cost)}</TableCell>
                <TableCell>{formatCurrency(e.actual_cost)}</TableCell>
                <TableCell><Badge variant="outline" className={STATUS_COLORS[e.status] || ""}>{e.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </div>
  );
}
